import React from "react";

/**
 * Circular crest / seal with a double-helix peptide ribbon at center —
 * mirroring the SmartPeptides.ai brand logo: gold + navy intertwined ribbons,
 * with beaded amino-acid residues at each rung.
 */
export const MedicallyCuratedCrest = ({ size = 168, className = "" }) => {
  const id = "crest-circle-path";

  return (
    <div
      className={`relative inline-block ${className}`}
      data-testid="medically-curated-crest"
      aria-label="Curated by medical professionals — for research and education purposes only"
      style={{ width: size, height: size }}
    >
      <svg viewBox="0 0 200 200" width={size} height={size} className="block">
        <defs>
          {/* Outer crest plating */}
          <radialGradient id="crest-bg" cx="50%" cy="45%" r="60%">
            <stop offset="0%" stopColor="#f6efd6" />
            <stop offset="55%" stopColor="#e7d49a" />
            <stop offset="100%" stopColor="#a88a28" />
          </radialGradient>
          <linearGradient id="crest-ring" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#c9a13d" />
            <stop offset="50%" stopColor="#8e7220" />
            <stop offset="100%" stopColor="#c9a13d" />
          </linearGradient>

          {/* Gold ribbon — metallic gradient running along the strand */}
          <linearGradient id="gold-ribbon" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fde8a8" />
            <stop offset="20%" stopColor="#e8c259" />
            <stop offset="50%" stopColor="#a88a28" />
            <stop offset="80%" stopColor="#d3a847" />
            <stop offset="100%" stopColor="#7a611a" />
          </linearGradient>

          {/* Navy ribbon — deep, with subtle blue highlight */}
          <linearGradient id="navy-ribbon" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3a4670" />
            <stop offset="25%" stopColor="#1f2a4a" />
            <stop offset="55%" stopColor="#0e1530" />
            <stop offset="80%" stopColor="#1f2a4a" />
            <stop offset="100%" stopColor="#0a1024" />
          </linearGradient>

          {/* Bead gradients — round + glossy */}
          <radialGradient id="bead-gold" cx="32%" cy="28%" r="75%">
            <stop offset="0%" stopColor="#fff7d6" />
            <stop offset="35%" stopColor="#e7c25a" />
            <stop offset="75%" stopColor="#a88a28" />
            <stop offset="100%" stopColor="#6c5614" />
          </radialGradient>
          <radialGradient id="bead-navy" cx="32%" cy="28%" r="75%">
            <stop offset="0%" stopColor="#6c7aa0" />
            <stop offset="45%" stopColor="#26314f" />
            <stop offset="100%" stopColor="#080d20" />
          </radialGradient>

          <path
            id={id}
            d="M 100,100 m -76,0 a 76,76 0 1,1 152,0 a 76,76 0 1,1 -152,0"
          />
        </defs>

        {/* Outer crest rings */}
        <circle cx="100" cy="100" r="96" fill="url(#crest-bg)" />
        <circle cx="100" cy="100" r="94" fill="none" stroke="url(#crest-ring)" strokeWidth="2.2" />
        <circle cx="100" cy="100" r="84" fill="none" stroke="#a88a28" strokeOpacity="0.45" strokeWidth="0.8" strokeDasharray="1 3" />
        <circle cx="100" cy="100" r="64" fill="#fbf7ea" />
        <circle cx="100" cy="100" r="64" fill="none" stroke="#a88a28" strokeOpacity="0.55" strokeWidth="1" />

        {/* Curved perimeter text */}
        <text fill="#5e4d18" fontSize="9.6" letterSpacing="1.6" fontFamily="Inter, sans-serif" fontWeight="600" textAnchor="middle">
          <textPath href={`#${id}`} startOffset="25%">
            CURATED BY MEDICAL PROFESSIONALS
          </textPath>
          <textPath href={`#${id}`} startOffset="75%">
            FOR RESEARCH &amp; EDUCATION ONLY
          </textPath>
        </text>

        {/* Separator dots */}
        <circle cx="20" cy="100" r="2" fill="#a88a28" />
        <circle cx="180" cy="100" r="2" fill="#a88a28" />

        {/* === Double-helix peptide chain === */}
        <g transform="translate(100 100)">
          {/* Rungs connecting the two strands (drawn first, behind) */}
          <g stroke="#8a6f1c" strokeWidth="0.9" opacity="0.45" strokeLinecap="round">
            <line x1="-17" y1="-40" x2="17" y2="-40" />
            <line x1="-17" y1="-20" x2="17" y2="-20" />
            <line x1="-17" y1="0" x2="17" y2="0" />
            <line x1="-17" y1="20" x2="17" y2="20" />
            <line x1="-17" y1="40" x2="17" y2="40" />
          </g>

          {/* ---- Z-ordered ribbon segments to create the wrap-around illusion ----
              Strand A (gold) and Strand B (navy) alternate which is "in front".
              Each segment is a quadratic curve covering one half-twist (~20px tall). */}

          {/* Segment 1 (y -40 → -20): GOLD in front, NAVY behind */}
          <path
            d="M 17 -40 Q 0 -30 -17 -20"
            fill="none"
            stroke="url(#navy-ribbon)"
            strokeWidth="4.2"
            strokeLinecap="round"
          />
          <path
            d="M -17 -40 Q 0 -30 17 -20"
            fill="none"
            stroke="url(#gold-ribbon)"
            strokeWidth="5"
            strokeLinecap="round"
          />

          {/* Segment 2 (y -20 → 0): NAVY in front */}
          <path
            d="M -17 -20 Q 0 -10 17 0"
            fill="none"
            stroke="url(#gold-ribbon)"
            strokeWidth="4.2"
            strokeLinecap="round"
          />
          <path
            d="M 17 -20 Q 0 -10 -17 0"
            fill="none"
            stroke="url(#navy-ribbon)"
            strokeWidth="5"
            strokeLinecap="round"
          />

          {/* Segment 3 (y 0 → 20): GOLD in front */}
          <path
            d="M 17 0 Q 0 10 -17 20"
            fill="none"
            stroke="url(#navy-ribbon)"
            strokeWidth="4.2"
            strokeLinecap="round"
          />
          <path
            d="M -17 0 Q 0 10 17 20"
            fill="none"
            stroke="url(#gold-ribbon)"
            strokeWidth="5"
            strokeLinecap="round"
          />

          {/* Segment 4 (y 20 → 40): NAVY in front */}
          <path
            d="M -17 20 Q 0 30 17 40"
            fill="none"
            stroke="url(#gold-ribbon)"
            strokeWidth="4.2"
            strokeLinecap="round"
          />
          <path
            d="M 17 20 Q 0 30 -17 40"
            fill="none"
            stroke="url(#navy-ribbon)"
            strokeWidth="5"
            strokeLinecap="round"
          />

          {/* Amino-acid bead pairs at each rung (gold + navy alternating sides) */}
          <g>
            {/* Top rung */}
            <circle cx="-17" cy="-40" r="4.4" fill="url(#bead-gold)" />
            <circle cx="-17" cy="-40" r="1.3" fill="#fff8e1" opacity="0.85" />
            <circle cx="17" cy="-40" r="4.4" fill="url(#bead-navy)" />
            <circle cx="17" cy="-40" r="1.1" fill="#fff" opacity="0.55" />

            {/* y = -20 */}
            <circle cx="-17" cy="-20" r="4.4" fill="url(#bead-navy)" />
            <circle cx="-17" cy="-20" r="1.1" fill="#fff" opacity="0.55" />
            <circle cx="17" cy="-20" r="4.4" fill="url(#bead-gold)" />
            <circle cx="17" cy="-20" r="1.3" fill="#fff8e1" opacity="0.85" />

            {/* Middle rung */}
            <circle cx="-17" cy="0" r="4.4" fill="url(#bead-gold)" />
            <circle cx="-17" cy="0" r="1.3" fill="#fff8e1" opacity="0.85" />
            <circle cx="17" cy="0" r="4.4" fill="url(#bead-navy)" />
            <circle cx="17" cy="0" r="1.1" fill="#fff" opacity="0.55" />

            {/* y = 20 */}
            <circle cx="-17" cy="20" r="4.4" fill="url(#bead-navy)" />
            <circle cx="-17" cy="20" r="1.1" fill="#fff" opacity="0.55" />
            <circle cx="17" cy="20" r="4.4" fill="url(#bead-gold)" />
            <circle cx="17" cy="20" r="1.3" fill="#fff8e1" opacity="0.85" />

            {/* Bottom rung */}
            <circle cx="-17" cy="40" r="4.4" fill="url(#bead-gold)" />
            <circle cx="-17" cy="40" r="1.3" fill="#fff8e1" opacity="0.85" />
            <circle cx="17" cy="40" r="4.4" fill="url(#bead-navy)" />
            <circle cx="17" cy="40" r="1.1" fill="#fff" opacity="0.55" />
          </g>
        </g>

        {/* Tiny stars between text segments */}
        <g fill="#a88a28">
          <polygon points="100,28 102,32 106,32 103,35 104,39 100,37 96,39 97,35 94,32 98,32" />
          <polygon points="100,172 102,168 106,168 103,165 104,161 100,163 96,161 97,165 94,168 98,168" />
        </g>
      </svg>
    </div>
  );
};
